import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Rocket, Wrench, Activity, Database, GitBranch, Workflow, ShieldCheck, Users } from "lucide-react";
import Reveal from "../components/site/Reveal";

const SERVICES = [
  {
    code: "01",
    title: "ERP Strategy & Advisory",
    Icon: Compass,
    body: "Platform selection, maturity assessment, business case and multi-year ERP roadmap — grounded in your P&L.",
    points: ["SAP · Oracle · Salesforce selection", "TCO & ROI modeling", "Process re-engineering", "Target operating model"],
  },
  {
    code: "02",
    title: "ERP Implementation",
    Icon: Rocket,
    body: "End-to-end delivery with senior functional and technical architects on every wave — from blueprint to hypercare.",
    points: ["SAP S/4HANA · BRIM full-stack", "Oracle CPQ · Subscription · RMCS", "Salesforce Revenue Cloud", "Wave-based cutovers"],
  },
  {
    code: "03",
    title: "Data Migration",
    Icon: Database,
    body: "Zero-drama ERP cutovers with reconciled ledgers, event-level lineage and revenue continuity guarantees.",
    points: ["Legacy ERP extracts", "Contract & subscription migration", "Mediation & rating", "Cutover runbooks"],
  },
  {
    code: "04",
    title: "Integration Architecture",
    Icon: Workflow,
    body: "Event-driven integration between CRM, mediation, ERP billing, finance and revenue recognition.",
    points: ["MuleSoft · OIC · SAP BTP", "API-led connectivity", "Kafka / EDA patterns", "Master data governance"],
  },
  {
    code: "05",
    title: "Revenue Assurance & Compliance",
    Icon: ShieldCheck,
    body: "Event-level assurance, ASC 606 / IFRS 15 revenue recognition, SOX controls and audit readiness.",
    points: ["Revenue leakage controls", "RevRec automation", "SOX / ICFR design", "Audit-ready reporting"],
  },
  {
    code: "06",
    title: "Managed Services (AMS)",
    Icon: Activity,
    body: "24×7 run, enhance and release management for SAP, Oracle and Salesforce ERPs post go-live.",
    points: ["L2 / L3 application support", "Enhancements backlog", "Release management", "SLA-driven operations"],
  },
  {
    code: "07",
    title: "ERP Modernisation",
    Icon: Wrench,
    body: "Upgrade, re-platform or consolidate legacy ERPs onto modern, cloud-native revenue platforms.",
    points: ["SAP RISE / GROW with SAP", "S/4HANA conversion", "Oracle Cloud migration", "Legacy decommissioning"],
  },
  {
    code: "08",
    title: "DevOps & Release",
    Icon: GitBranch,
    body: "Productised release pipelines for SAP, Oracle Fusion and Salesforce Revenue Cloud.",
    points: ["CI/CD for SAP & Salesforce", "Automated regression", "Environment strategy", "Config-as-code"],
  },
  {
    code: "09",
    title: "OTC Staffing",
    Icon: Users,
    body: "On-demand Order-to-Cash specialists — SAP BRIM, Oracle Fusion and Salesforce consultants you can plug into your programme in days, not months.",
    points: ["BRIM functional & technical consultants", "Oracle CPQ · RMCS · OSM experts", "Salesforce Revenue Cloud specialists", "Contract, contract-to-hire, or permanent"],
  },
];

const Services = () => {
  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] overflow-hidden pt-24 md:pt-32 pb-12 md:pb-20 border-b border-white/5" data-testid="services-hero">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div
          className="absolute inset-0 opacity-[0.18] mix-blend-screen"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/13156181/pexels-photo-13156181.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-xp-bg" />
        <div className="container-xp relative">
          <div className="eyebrow-lg mb-5">ERP Services &amp; Staffing</div>
          <h1 className="font-display tracking-display text-[40px] md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl" data-testid="services-hero-title">
            Nine disciplines. <br />
            <span className="text-xp-cyan">One</span> ERP outcome.
          </h1>
          <p className="mt-8 text-xp-muted max-w-2xl">
            We sell ERP outcomes, not headcount — but when you need headcount, we have the OTC bench. Every engagement is
            scoped against a measurable revenue, cost or compliance metric, and structured so value compounds long after
            we leave.
          </p>
        </div>
      </section>

      {/* Services grid */}
      <section className="section-xp" data-testid="services-grid-section">
        <div className="container-xp">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5" data-testid="services-grid">
            {SERVICES.map((s, i) => (
              <Reveal
                key={s.code}
                delay={(i % 3) * 0.05}
                className="group bg-xp-bg p-8 lg:p-10 transition-colors hover:bg-xp-surface relative"
                data-testid={`services-card-${s.code}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono text-xp-dim text-xs">{s.code}</div>
                  <s.Icon className="h-5 w-5 text-xp-muted group-hover:text-xp-cyan transition-colors" />
                </div>
                <h3 className="mt-10 font-display text-2xl tracking-display">{s.title}</h3>
                <p className="mt-4 text-sm text-xp-muted">{s.body}</p>
                <ul className="mt-6 space-y-1.5 text-xs text-xp-muted">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-xp-cyan" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-xp border-t border-white/5" data-testid="services-process">
        <div className="container-xp">
          <Reveal className="max-w-xl mb-14">
            <div className="eyebrow mb-4">Our Method</div>
            <h2 className="font-display tracking-display text-[32px] md:text-5xl">The <span className="brand">Xpace</span> delivery loop.</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              ["Discover", "Rapid assessment of process, data and platforms."],
              ["Design", "Opinionated target architecture & delivery plan."],
              ["Deliver", "Waves. Cutovers. Hypercare. No surprises."],
              ["Operate", "Run, improve and measure — every quarter."]
            ].map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.06} className="border-t border-white/15 pt-6">
                <div className="font-mono text-xp-cyan text-xs">0{i + 1}</div>
                <div className="font-display text-2xl mt-2 tracking-display">{t}</div>
                <p className="mt-3 text-sm text-xp-muted">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-xp">
        <div className="container-xp">
          <Reveal className="relative overflow-hidden border border-white/10 rounded-sm p-10 md:p-16 bg-xp-surface">
            <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
            <div className="relative grid md:grid-cols-5 gap-10 items-center">
              <div className="md:col-span-3">
                <div className="eyebrow mb-4">Start here</div>
                <h3 className="font-display text-3xl md:text-5xl tracking-display">
                  Not sure which service fits?
                </h3>
                <p className="mt-4 text-xp-muted max-w-xl">
                  Book a 30-minute discovery call — we'll map your current stack and propose a right-sized engagement.
                </p>
              </div>
              <div className="md:col-span-2 flex md:justify-end">
                <Link to="/contact" className="btn-cyan" data-testid="services-cta">
                  Book a discovery call <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Services;
